import { useState, useEffect, useMemo } from 'react';
import { X, UserPlus, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Avatar } from '../../ui/Avatar';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { useNotification } from '@/hooks/useNotification';
import { useAppSelector } from '@/store/hooks';
import {
  getAllUsers,
  getWorkspaceUsers,
  addNewUser,
  updateUserRole,
  removeUserFromWorkspace,
  removeInvitedUser,
} from '../../../apiServices/userApi';
import { validateEmailFormat } from '../../../util/UtilFunctions';
import { hasExpired } from '../../../util/subscriptionDetails';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DynUser = any;

type RoleId = 'admin' | 'pro' | 'user' | 'viewer';
const ROLE_OPTIONS: { id: RoleId; label: string }[] = [
  { id: 'admin', label: 'Admin' },
  { id: 'pro', label: 'Pro' },
  { id: 'user', label: 'User' },
  { id: 'viewer', label: 'Viewer' },
];

function getAvailableRoles(currentRole: string, isRootWorkspace: boolean): RoleId[] {
  if (currentRole === 'admin') return isRootWorkspace ? ['admin', 'pro', 'user', 'viewer'] : ['pro', 'user', 'viewer'];
  if (currentRole === 'pro') return ['user', 'viewer'];
  return [];
}

function roleBadgeVariant(role: string) {
  if (role === 'admin' || role === 'pro') return 'default' as const;
  return 'outline' as const;
}

function getUserAttr(user: DynUser, attr: string): string {
  return user.cognitoAttributes?.UserAttributes?.find((a: { Name: string }) => a.Name === attr)?.Value ?? '';
}

// ── Props ───────────────────────────────────────────────────────────

interface WorkspaceParticipiantsModalProps {
  closeModal: () => void;
  groupId: string;
  updateIsQuerying: (q: boolean) => void;
  currentUserInfo: DynUser;
  getWorkspacePeopleList?: () => void;
  refreshList?: (...args: unknown[]) => void;
}

// ── Main Component ──────────────────────────────────────────────────

export default function WorkspaceParticipiantsModal({
  closeModal,
  groupId,
  updateIsQuerying,
  currentUserInfo,
  getWorkspacePeopleList,
  refreshList,
}: WorkspaceParticipiantsModalProps) {
  const { notify } = useNotification();
  const allWorkspaces = useAppSelector((state) => state.workspace.allWorkspaces);

  const [people, setPeople] = useState<DynUser[]>([]);
  const [invitedUsers, setInvitedUsers] = useState<DynUser[]>([]);
  const [allPeople, setAllPeople] = useState<DynUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionsOpen, setSectionsOpen] = useState({ people: true, addExisting: false, addNew: false });

  const workspace = allWorkspaces.find((w) => w.id === groupId) ?? allWorkspaces.find((w) => (w as DynUser).id?.S === groupId);
  const isRootWorkspace = workspace?.parent === 'root';
  const expired = hasExpired(workspace);

  // Current user's role in this workspace
  const currentUserRole = useMemo(() => {
    const userGroup = currentUserInfo?.groups?.find((g: DynUser) => g.id.S === groupId);
    return userGroup?.role?.S ?? 'user';
  }, [currentUserInfo, groupId]);

  const isAdminOrPro = currentUserRole === 'admin' || currentUserRole === 'pro';

  // Fetch people
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await getWorkspaceUsers(groupId);
        const items = res.response?.Items ?? [];
        const invited = res.response?.invitedUsers ?? [];
        setPeople(items);
        setInvitedUsers(invited);
      } catch {
        notify({ type: 'error', message: 'Could not load participants' });
      }

      // Fetch all users for "add existing" section
      if (isAdminOrPro) {
        try {
          const allRes = await getAllUsers();
          setAllPeople(allRes.response?.Items ?? []);
        } catch { /* ignore */ }
      }
      setLoading(false);
    }
    fetchData();
  }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // People not already in this workspace
  const availablePeople = useMemo(() => {
    const currentUids = new Set(people.map((p) => p.user?.S));
    const invitedEmails = new Set(invitedUsers.map((u) => u.email?.S));
    return allPeople.filter((p) => {
      const uid = p.user?.S;
      const email = getUserAttr(p, 'email');
      return uid && !currentUids.has(uid) && !invitedEmails.has(email);
    });
  }, [people, invitedUsers, allPeople]);

  const toggleSection = (key: keyof typeof sectionsOpen) => {
    setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      updateIsQuerying(true);
      await removeUserFromWorkspace(userId, groupId);
      updateIsQuerying(false);
      setPeople((prev) => prev.filter((p) => p.user?.S !== userId));
      notify({ type: 'success', message: 'User removed' });
      getWorkspacePeopleList?.();
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not remove user' });
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      updateIsQuerying(true);
      await updateUserRole(userId, newRole, groupId);
      updateIsQuerying(false);
      setPeople((prev) => prev.map((p) => p.user?.S === userId ? { ...p, role: { S: newRole } } : p));
      notify({ type: 'success', message: 'Role updated' });
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not update role' });
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await removeInvitedUser(inviteId);
      setInvitedUsers((prev) => prev.filter((u) => u.id?.S !== inviteId));
      notify({ type: 'success', message: 'Invitation revoked' });
    } catch {
      notify({ type: 'error', message: 'Could not revoke invitation' });
    }
  };

  const handleAddExistingUser = async (user: DynUser, role: string) => {
    try {
      updateIsQuerying(true);
      await addNewUser(getUserAttr(user, 'email'), role, groupId);
      updateIsQuerying(false);
      // Move from available to people list
      setAllPeople((prev) => prev.filter((p) => p.user?.S !== user.user?.S));
      setPeople((prev) => [...prev, { ...user, role: { S: role } }]);
      notify({ type: 'success', message: `Added ${getUserAttr(user, 'given_name')}` });
      getWorkspacePeopleList?.();
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not add user' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const availableRoles = getAvailableRoles(currentUserRole, isRootWorkspace);
  const sortedPeople = [...people].sort((a, b) => getUserAttr(a, 'given_name').localeCompare(getUserAttr(b, 'given_name')));

  return (
    <div className="space-y-5">
      {/* ── Current participants ── */}
      <Section title="People" open={sectionsOpen.people} onToggle={() => toggleSection('people')}>
        <ul className="space-y-1">
          {sortedPeople.map((person) => {
            const firstName = getUserAttr(person, 'given_name') || person.firstname?.S || '';
            const lastName = getUserAttr(person, 'family_name') || person.lastname?.S || '';
            const email = getUserAttr(person, 'email') || person.email?.S || '';
            const role = person.role?.S ?? 'user';
            const img = person.image?.S;
            const uid = person.user?.S;
            const isCreator = person.creator?.N === '1';
            const canEdit = isAdminOrPro && role !== 'admin' && !isCreator;

            return (
              <li key={uid} className="flex items-center gap-3 rounded-md px-2 py-1.5">
                <Avatar src={img} name={`${firstName} ${lastName}`} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-accent-foreground">
                      {firstName} {lastName}
                      {isCreator && <span className="ml-1 text-xs text-muted-foreground">(Owner)</span>}
                    </span>
                    <Badge variant={roleBadgeVariant(role)}>{role}</Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{email}</p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <RoleDropdown currentRole={role} availableRoles={availableRoles} onChange={(r) => handleRoleChange(uid, r)} />
                    <button
                      onClick={() => handleRemoveUser(uid)}
                      className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground"
                      title="Remove"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Section>

      {/* ── Pending invitations ── */}
      {invitedUsers.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending invitations</h3>
          <ul className="space-y-1">
            {invitedUsers.map((inv) => (
              <li key={inv.id?.S || inv.email?.S} className="flex items-center justify-between rounded-md px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-accent-foreground">{inv.email?.S}</span>
                  <Badge variant={roleBadgeVariant(inv.role?.S)}>{inv.role?.S}</Badge>
                </div>
                {isAdminOrPro && inv.id?.S && (
                  <button
                    onClick={() => handleRevokeInvite(inv.id.S)}
                    className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground"
                    title="Revoke"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Add existing people ── */}
      {isAdminOrPro && availablePeople.length > 0 && (
        <Section title="Add from other workspaces" open={sectionsOpen.addExisting} onToggle={() => toggleSection('addExisting')}>
          <ul className="space-y-1">
            {availablePeople.map((person) => {
              const firstName = getUserAttr(person, 'given_name');
              const lastName = getUserAttr(person, 'family_name');
              const email = getUserAttr(person, 'email');
              return (
                <AddExistingRow
                  key={person.user?.S}
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  img={person.image?.S}
                  availableRoles={availableRoles}
                  onAdd={(role) => handleAddExistingUser(person, role)}
                />
              );
            })}
          </ul>
        </Section>
      )}

      {/* ── Invite new person ── */}
      {isAdminOrPro && (
        <Section title="Add new people" open={sectionsOpen.addNew} onToggle={() => toggleSection('addNew')}>
          <InviteForm groupId={groupId} availableRoles={availableRoles} onInvited={(inv) => setInvitedUsers((prev) => [...prev, inv])} />
        </Section>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function Section({ title, open, onToggle, children }: {
  title: string; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <section>
      <button
        type="button"
        onClick={onToggle}
        className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-muted-foreground"
      >
        <Icon className="size-3" /> {title}
      </button>
      {open && children}
    </section>
  );
}

function RoleDropdown({ currentRole, availableRoles, onChange }: {
  currentRole: string; availableRoles: RoleId[]; onChange: (role: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!availableRoles.length) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-secondary"
      >
        {currentRole} <ChevronDown className="ml-0.5 inline size-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-28 rounded-xl border border-border/60 bg-card py-1 shadow-xl">
          {availableRoles.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => { onChange(role); setOpen(false); }}
              className={`w-full px-3 py-1 text-left text-xs hover:bg-secondary ${role === currentRole ? 'font-semibold text-primary' : 'text-accent-foreground'}`}
            >
              {role}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddExistingRow({ firstName, lastName, email, img, availableRoles, onAdd }: {
  firstName: string; lastName: string; email: string; img?: string;
  availableRoles: RoleId[]; onAdd: (role: string) => void;
}) {
  const [selectedRole, setSelectedRole] = useState<RoleId>(availableRoles[availableRoles.length - 1] ?? 'viewer');
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  if (added) return null;

  return (
    <li className="flex items-center gap-3 rounded-md px-2 py-1.5">
      <Avatar src={img} name={`${firstName} ${lastName}`} size="sm" />
      <div className="min-w-0 flex-1">
        <span className="truncate text-sm font-medium text-accent-foreground">{firstName} {lastName}</span>
        <p className="truncate text-xs text-muted-foreground">{email}</p>
      </div>
      <select
        value={selectedRole}
        onChange={(e) => setSelectedRole(e.target.value as RoleId)}
        className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground"
      >
        {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <Button size="sm" onClick={async () => { setAdding(true); await onAdd(selectedRole); setAdded(true); }} disabled={adding}>
        {adding ? <Loader2 className="size-3.5 animate-spin" /> : <UserPlus className="mr-1 size-3.5" />}
        Add
      </Button>
    </li>
  );
}

function InviteForm({ groupId, availableRoles, onInvited }: {
  groupId: string; availableRoles: RoleId[];
  onInvited: (inv: DynUser) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoleId>(availableRoles[availableRoles.length - 1] ?? 'viewer');
  const [submitting, setSubmitting] = useState(false);
  const { notify } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { notify({ type: 'info', message: 'Please enter an email address' }); return; }
    if (!validateEmailFormat(trimmed)) { notify({ type: 'info', message: 'Invalid email format' }); return; }

    setSubmitting(true);
    try {
      const res = await addNewUser(trimmed, role, groupId);
      if (res.response?.includes?.('ERROR')) throw new Error();
      onInvited({ email: { S: trimmed }, role: { S: role }, id: { S: `inv-${Date.now()}` } });
      notify({ type: 'success', message: `Invited ${trimmed} as ${role}` });
      setEmail('');
    } catch {
      notify({ type: 'error', message: 'Could not invite user' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        {availableRoles.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRole(r)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
              role === r ? 'border-primary bg-primary-muted text-primary-hover' : 'border-border text-muted-foreground hover:border-border'
            }`}
          >
            {r}
          </button>
        ))}
      </div>
      <Button type="submit" size="sm" disabled={submitting}>
        {submitting ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : <UserPlus className="mr-1 size-3.5" />}
        Add
      </Button>
    </form>
  );
}
